import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const TOKEN_KEY = "legacylink:access_token";

export function setStoredToken(token) {
  try {
    if (token) {
      localStorage.setItem(TOKEN_KEY, token);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
  } catch (err) {
    // localStorage can throw in private mode — fall back silently.
    console.warn("Token storage unavailable:", err);
  }
}

export function getStoredToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (err) {
    console.warn("Token storage read failed:", err);
    return null;
  }
}

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Safari (ITP) and other browsers sometimes drop the cross-site `access_token`
// cookie inside the preview iframe. As a belt-and-braces fix the backend also
// returns the raw token; we stash it in localStorage and resend it as a
// Bearer header so the session works regardless of cookie policy.
api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers = config.headers || {};
    if (!config.headers.Authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    const url = error?.config?.url || "";
    const status = error?.response?.status;
    if (
      status === 401 &&
      !url.includes("/auth/me") &&
      !url.includes("/auth/login") &&
      !url.includes("/auth/register")
    ) {
      try {
        window.dispatchEvent(new CustomEvent("legacylink:auth-expired"));
      } catch (dispatchErr) {
        console.warn("Failed to dispatch auth-expired event:", dispatchErr);
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(input) {
  // Accept either a FastAPI-style "detail" payload OR a raw axios error.
  // Returns the most informative, user-readable string we can derive.
  if (input == null) return "Something went wrong. Please try again.";

  // Raw axios error → unwrap.
  if (input && typeof input === "object" && (input.isAxiosError || input.response || input.request)) {
    if (input.response) {
      const status = input.response.status;
      const body = input.response.data;
      if (body && typeof body === "object") {
        if (body.detail !== undefined) return formatApiError(body.detail);
        if (typeof body.message === "string") return body.message;
        if (typeof body.error === "string") return body.error;
      }
      if (typeof body === "string" && body.trim()) return body;
      // Status-specific fallbacks so the user gets something actionable.
      if (status === 401) return "Please sign in again.";
      if (status === 403) return "You don't have permission to do that.";
      if (status === 404) return "We couldn't find what you were looking for.";
      if (status === 409) return "That conflicts with something already on file.";
      if (status === 413) return "That file is too large.";
      if (status === 429) return "Slow down a moment, then try again.";
      if (status >= 500) return "Our server hit a problem. Please try again in a moment.";
      return `Request failed (${status}).`;
    }
    if (input.request) return "Couldn't reach the server. Check your connection and try again.";
    return input.message || "Something went wrong. Please try again.";
  }

  // Detail payload paths.
  if (typeof input === "string") return input;
  if (Array.isArray(input))
    return input.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (input && typeof input.msg === "string") return input.msg;
  return String(input);
}

export function fileUrl(path) {
  if (!path) return null;
  return `${API}/files/${path}`;
}
